import { SiteState, RootState, Site } from '@/types';
import { GetterTree, MutationTree, ActionTree, Module } from 'vuex';
import SiteService from '@/services/site.service';
import { SiteMutator } from '@/mutators/site.mutator';

type SiteGetter = GetterTree<SiteState, RootState>;

export const initialState = (): SiteState => {
  return {
    initialState,
    sites: [],
    filters: {},
    total: 0,
  };
};

export const getters: SiteGetter = {
  sites: (state) => state,
  siteFilters: (state) => state.filters,
  sitesForProject: (state) => (projectId: number) => state.sites.filter((item: Site) => item.project_id === projectId),
  sitesTabular: (state) => (config: any) => state.sites.map((site) => SiteMutator.toTabular(site, config)),
  getSiteById: (state) => (id: number) => state.sites.find((item) => item.id === Number(id)),
};

export const mutations: MutationTree<SiteState> = {
  addSites(state, newSites) {
    state.total = newSites.total;
    state.sites = newSites.sites;
    state.filters = newSites.columnFilters;
  },
  addSite(state, newSite) {
    state.total = state.total + 1;
    state.sites.push(newSite);
  },
  deleteSite(state, deleteSite) {
    state.total = state.total--;
    state.sites = state.sites.filter((site: Site) => site.id !== deleteSite.id);
  },
  updateSite(state, updatedSite: Site) {
    state.sites[state.sites.findIndex((site: Site) => site.id === updatedSite.id)] = updatedSite;
  },
  upsertSite(state, newSite: Site) {
    const siteIndex = state.sites.findIndex((site: Site) => site.id === newSite.id);
    if (siteIndex >= 0) {
      state.sites[siteIndex] = newSite;
    } else {
      state.total++;
      state.sites.push(newSite);
    }
  },
};

export const actions: ActionTree<SiteState, RootState> = {
  async addSitesAsync(context: any, pagination: {
    take: number, skip: number, sort: string,
    desc: boolean, filters: Record<string, Record<string, boolean>>, projectId: number,
    searchNames: string,
  }) {
    const sitesResp = await SiteService.getSites(
      pagination.take, pagination.skip, pagination.sort,
      pagination.desc, pagination.filters, pagination.projectId,
      pagination.searchNames,
    );
    context.commit('addSites', sitesResp);
    return sitesResp;
  },
  async addSiteAsync(context: any, id: number) {
    const site = await SiteService.getSite(id);
    context.commit('upsertSite', site);
    return site;
  },
  async createSiteAsync(context, site) {
    const createdSite = await SiteService.createSite(site);
    context.commit('addSite', createdSite);
    return createdSite;
  },
  async createSitesAsync(context, sitesBatch) {
    const createdSites = await SiteService.createSites(sitesBatch);
    // We can't programtically add to the sites store - this will break pagination
    // context.commit('addSites', response.data);
    return createdSites;
  },
  async deleteSiteAsync(context, site) {
    const response = await SiteService.deleteSite(site);
    context.commit('deleteSite', response);
    return response;
  },
  async updateSiteAsync(context: any, site: { id: number, payload: object }) {
    const updatedSite = await SiteService.updateSite(site.id, site.payload);
    context.commit('updateSite', updatedSite);
    return updatedSite;
  },
};

export const sites: Module<SiteState, RootState> = {
  state: initialState(),
  getters,
  mutations,
  actions,
};
